import {fromJS, List} from 'immutable';
import QuestionTypeStore from 'components/QuestionTypes/QuestionType.store';
import {debounce} from 'lodash';

import {
  blankSlateValue,
  convertStateToPayload,
  convertStateToString,
  convertStateToImgArr
} from './WrittenSubmission/Editor/Utils/editor.utils';

import freeResponseActions from './FreeResponseQuestion.actions';
import {saveDraft, getDraft, patchDraft} from './FreeResponseQuestion.queries';
import {saveStatusType, DraftResponse} from './FreeResponseQuestion.types';

const excludedDraftViews = ['folder', 'dennis'];

const getAssignmentId = () => {
  const params = global.location.pathname.split('/');

  let assignmentId = null;

  if (params.some((param) => param === 'assignment')) {
    assignmentId = params[params.length - 1];
  }

  return assignmentId;
};

const shouldNotSaveDraft = () => {
  const params = global.location.pathname.split('/');

  return params.some((param) => excludedDraftViews.includes(param));
};

export default class FreeResponseQuestionStore extends QuestionTypeStore {
  constructor(name, questionId, question) {
    super(name);
    const assignmentId = getAssignmentId();
    this.initialData = fromJS({
      hasUnsavedEdits: false,
      timeElapsed: 0,
      editStartTime: null,
      lastDraftTime: null,
      saveStatus: saveStatusType.default,
      questionId,
      draftId: null,
      guessId: null,
      assignmentId,
      shouldShowExplanation: false,
      editorValue: blankSlateValue
    });
    this.setInitialData(this.initialData);
    this.writeData('__question', question);
    this.handleTargeted(freeResponseActions.UPDATE_RESPONSE, this.updateInput);
    this.handleTargeted(freeResponseActions.LOAD_DRAFT, this.loadDraft);
    this.handleTargeted(freeResponseActions.SAVE_DRAFT, this.saveDraft);
    this.handleTargeted(freeResponseActions.LOAD_GUESS, this.loadGuess);
    this.handleTargeted(freeResponseActions.CANCEL_AUTO_SAVE, this.cancelDraftSave);
    this.handleTargeted(freeResponseActions.START_TIMER, this.startTimer);
    this.handleTargeted(freeResponseActions.SAVE_CALLBACK, this.saveDraftCallback);
    this.debouncedSave = debounce(this.saveDraft.bind(this), 5000);
  }

  updateInput(data) {
    this.writeData('editorValue', data);
    const hasUnsavedEdits = this.readData('hasUnsavedEdits');
    this.debouncedSave();
    if (hasUnsavedEdits) return;
    this.writeData('hasUnsavedEdits', true);
  }

  startTimer() {
    const editStartTime = new Date();
    this.writeData('editStartTime', editStartTime);
  }

  resetTimer() {
    this.writeData('hasUnsavedEdits', false);
    this.startTimer();
  }

  clearTimer() {
    this.writeData('editStartTime', null);
  }

  cancelDraftSave() {
    this.debouncedSave.cancel();
  }

  async loadDraft() {
    if (shouldNotSaveDraft()) {
      return;
    }
    const assignmentId = this.readData('assignmentId');
    const questionId = this.readData('questionId');
    let response: List<DraftResponse> | null = null;
    try {
      response = await getDraft(questionId, assignmentId);
    } catch (err) {
      console.log(err);
      this.writeData('saveStatus', saveStatusType.loadError);
      // todo how to handle loading draft errors
      return;
    }

    if (response === null || response.size === 0) {
      return;
    }

    const draftGuess = response.first();
    const rawData = draftGuess.getIn(['content', 'raw'], null);
    const id = draftGuess.get('id', null);
    const timeElapsed = draftGuess.get('time_elapsed', 0);
    const updatedAt = draftGuess.get('updated_at', null);
    const blocks = JSON.parse(rawData);

    if (rawData) {
      this.writeData('editorValue', blocks);
      this.writeData('draftId', id);
      this.writeData('timeElapsed', timeElapsed);
      this.writeData('saveStatus', saveStatusType.saved);
      this.writeData('lastDraftTime', updatedAt);
    }
  }

  async saveDraft() {
    if (shouldNotSaveDraft()) {
      return;
    }
    const hasUnsavedEdits = this.readData('hasUnsavedEdits');
    if (!hasUnsavedEdits) return;
    const rawData = this.readData('editorValue');
    const content = convertStateToPayload(rawData);

    const draftId = this.readData('draftId');
    const assignmentId = this.readData('assignmentId');
    const questionId = this.readData('questionId');

    const editStartTime = this.readData('editStartTime');
    const prevTimeElapsed = this.readData('timeElapsed');
    const now = new Date();

    const newTimeElapsed = editStartTime ? now - editStartTime : 0;

    const totalTime = newTimeElapsed + prevTimeElapsed;
    const payload = {timeElapsed: totalTime, content};
    this.writeData('saveStatus', saveStatusType.saving);
    let response;
    try {
      if (draftId) {
        response = await patchDraft(draftId, payload);
      } else {
        response = await saveDraft(payload, questionId, assignmentId);
        const id = response.get('id', null);
        this.writeData('draftId', id);
      }
    } catch (err) {
      // todo: add error toast that there was an error saving?
      // or should this function take an error callback.
      // add error to save status
      this.writeData('saveStatus', saveStatusType.saveError);
      console.log(err);
      return;
    }
    this.resetTimer();

    const updatedAt = response.get('updated_at', JSON.stringify(new Date()));
    const timeElapsed = response.get('time_elapsed', 0);
    // TODO: error handling for draft saving
    this.writeData('saveStatus', saveStatusType.saved);
    this.writeData('lastDraftTime', updatedAt);
    this.writeData('timeElapsed', timeElapsed);
  }

  async saveDraftCallback(cb) {
    this.writeData('hasUnsavedEdits', true);
    await this.saveDraft();
    const draftId = this.readData('draftId');
    cb(draftId);
  }

  loadGuess(guess) {
    super._loadGuess(guess);

    const rawData = guess.getIn(['content', 'raw'], null);
    if (rawData) {
      this.writeData('editorValue', JSON.parse(rawData));
      this.writeData('guessId', guess.id);
    }
  }

  getLastSaveTime() {
    const saveTime = this.readData('lastDraftTime');
    return saveTime ? new Date(saveTime) : undefined;
  }

  getSaveStatus() {
    return this.readData('saveStatus');
  }

  getHasUnsavedEdits() {
    return this.readData('hasUnsavedEdits');
  }

  getWordCount() {
    const rawData = this.readData('editorValue');
    const text = convertStateToString(rawData);
    return text.split(' ').filter((s) => !!s.length).length;
  }

  buildGuessContent() {
    this.writeData('hasUnsavedEdits', false);
    const rawData = this.readData('editorValue');
    const content = convertStateToPayload(rawData);

    return content;
  }

  buildTimeElapsed() {
    const editStartTime = this.readData('editStartTime');
    const prevTimeElapsed = this.readData('timeElapsed');
    const now = new Date();

    const newTimeElapsed = editStartTime ? now - editStartTime : 0;
    const totalTime = newTimeElapsed + prevTimeElapsed;

    return totalTime;
  }

  get correctAnswer() {
    return this.validResponse.keySeq().toSet();
  }
  /**
   * For a "free-response" question, the user submits an answer to be graded outside of albert.
   * Although they simply "Mark as Complete" we have a one word minimum for written student responses
   */

  get hasUserSelectedAnswer() {
    const rawData = this.readData('editorValue');
    const text = convertStateToString(rawData);
    const images = convertStateToImgArr(rawData);
    const words = text.split(' ').filter((s) => !!s.length);

    return words.length > 0 || images.length > 0;
  }

  getValue() {
    const value = this.readData('editorValue');
    return List.isList(value) ? value.toJS() : value;
  }

  getDraftId() {
    const draftId = this.readData('draftId');
    return draftId;
  }

  getGuessId() {
    const guessId = this.readData('guessId');
    return guessId;
  }
}
